'use client';

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

type PopupFormProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function PopupForm({ isOpen, onClose }: PopupFormProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setName('');
    setPhone('');
    setEmail('');
    setMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const res = await fetch('/send-mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          phone,
          email,
          message,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Something went wrong');
      }

      setShowThankYou(true);
      resetForm();

      setTimeout(() => {
        setShowThankYou(false);
        onClose();
      }, 2500);
    } catch (error) {
      console.error('Send mail error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShowThankYou(false);
    }
  }, [isOpen]);

  return (
    <AnimatePresence data-component-id="src/components/ui/PopupForm.tsx:81:4" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="81" data-component-end-line="185" data-component-file="PopupForm.tsx" data-component-name="AnimatePresence" data-component-content="%7B%22elementName%22%3A%22AnimatePresence%22%7D">
      {isOpen && (
        <motion.div data-component-id="src/components/ui/PopupForm.tsx:83:8" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="83" data-component-end-line="183" data-component-file="PopupForm.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22fixed%20inset-0%20z-%5B100%5D%20flex%20items-center%20justify-center%20bg-black%2F70%20px-4%20backdrop-blur-sm%22%7D"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          {!showThankYou ? (
            <motion.div data-component-id="src/components/ui/PopupForm.tsx:91:12" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="91" data-component-end-line="167" data-component-file="PopupForm.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22h-%5B90vh%5D%20overflow-y-auto%20relative%20w-full%20max-w-%5B560px%5D%20rounded-%5B28px%5D%20border%20border-white%2F10%20bg-%5B%233a3a3a%5D%2F95%20p-6%20text-%5B%23b3b3b3%5D%20shadow-%5B0_20px_80px_rgba(0%2C0%2C0%2C0.45)%5D%20sm%3Ap-8%22%7D"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              transition={{ duration: 0.25 }}
              className="h-[90vh] overflow-y-auto relative w-full max-w-[560px] rounded-[28px] border border-white/10 bg-[#3a3a3a]/95 p-6 text-[#b3b3b3] shadow-[0_20px_80px_rgba(0,0,0,0.45)] sm:p-8"
            >
              <button data-component-id="src/components/ui/PopupForm.tsx:99:14" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="99" data-component-end-line="105" data-component-file="PopupForm.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-4%20top-4%20flex%20h-10%20w-10%20items-center%20justify-center%20rounded-full%20border%20border-white%2F10%20text-xl%20text-white%2F60%20transition%20hover%3Abg-white%2F5%20hover%3Atext-white%22%2C%22textContent%22%3A%22%C3%97%22%7D"
                onClick={onClose}
                aria-label="Close popup"
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-xl text-white/60 transition hover:bg-white/5 hover:text-white"
              >
                ×
              </button>

              <p data-component-id="src/components/ui/PopupForm.tsx:107:14" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="107" data-component-end-line="107" data-component-file="PopupForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22pr-10%20text-%5B18px%5D%20sm%3Atext-%5B20px%5D%20text-white%2F80%22%2C%22textContent%22%3A%22Contact%20us%22%7D" className="pr-10 text-[18px] sm:text-[20px] text-white/80">Contact us</p>

              <h3 data-component-id="src/components/ui/PopupForm.tsx:109:14" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="109" data-component-end-line="111" data-component-file="PopupForm.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22mt-2%20pr-10%20text-%5B34px%5D%20font-bold%20leading-%5B1.1%5D%20text-white%20sm%3Atext-%5B42px%5D%22%2C%22textContent%22%3A%22Let%E2%80%99s%20discuss%20your%20project%22%7D" className="mt-2 pr-10 text-[34px] font-bold leading-[1.1] text-white sm:text-[42px]">
                Let’s discuss your project
              </h3>

              <p data-component-id="src/components/ui/PopupForm.tsx:113:14" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="113" data-component-end-line="115" data-component-file="PopupForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-4%20max-w-%5B460px%5D%20text-%5B14px%5D%20leading-%5B1.6%5D%20text-white%2F65%20sm%3Atext-%5B16px%5D%22%2C%22textContent%22%3A%22Leave%20your%20details%20and%20we%20will%20get%20back%20to%20you%20as%20soon%20as%20possible.%22%7D" className="mt-4 max-w-[460px] text-[14px] leading-[1.6] text-white/65 sm:text-[16px]">
                Leave your details and we will get back to you as soon as possible.
              </p>

              <form data-component-id="src/components/ui/PopupForm.tsx:117:14" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="117" data-component-end-line="166" data-component-file="PopupForm.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22mt-6%20space-y-4%22%7D" onSubmit={handleSubmit} className="mt-6 space-y-4">
                <input data-component-id="src/components/ui/PopupForm.tsx:118:16" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="118" data-component-end-line="126" data-component-file="PopupForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22name%22%3A%22name%22%2C%22value%22%3A%22%5Bvar%3Aname%5D%22%2C%22className%22%3A%22w-full%20rounded-xl%20border%20border-white%2F10%20bg-white%20px-4%20py-3%20text-black%20placeholder%3Atext-gray-500%20outline-none%20transition%20focus%3Aborder-%5B%239000ff%5D%22%7D"
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full rounded-xl border border-white/10 bg-white px-4 py-3 text-black placeholder:text-gray-500 outline-none transition focus:border-[#9000ff]"
                />

                <input data-component-id="src/components/ui/PopupForm.tsx:128:16" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="128" data-component-end-line="137" data-component-file="PopupForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22tel%22%2C%22name%22%3A%22phone%22%2C%22value%22%3A%22%5Bvar%3Aphone%5D%22%2C%22className%22%3A%22w-full%20rounded-xl%20border%20border-white%2F10%20bg-white%20px-4%20py-3%20text-black%20placeholder%3Atext-gray-500%20outline-none%20transition%20focus%3Aborder-%5B%239000ff%5D%22%7D"
                  type="tel"
                  name="phone"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  inputMode="tel"
                  className="w-full rounded-xl border border-white/10 bg-white px-4 py-3 text-black placeholder:text-gray-500 outline-none transition focus:border-[#9000ff]"
                />

                <input data-component-id="src/components/ui/PopupForm.tsx:139:16" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="139" data-component-end-line="147" data-component-file="PopupForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22name%22%3A%22email%22%2C%22value%22%3A%22%5Bvar%3Aemail%5D%22%2C%22className%22%3A%22w-full%20rounded-xl%20border%20border-white%2F10%20bg-white%20px-4%20py-3%20text-black%20placeholder%3Atext-gray-500%20outline-none%20transition%20focus%3Aborder-%5B%239000ff%5D%22%7D"
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-xl border border-white/10 bg-white px-4 py-3 text-black placeholder:text-gray-500 outline-none transition focus:border-[#9000ff]"
                />

                <textarea data-component-id="src/components/ui/PopupForm.tsx:149:16" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="149" data-component-end-line="157" data-component-file="PopupForm.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22name%22%3A%22message%22%2C%22value%22%3A%22%5Bvar%3Amessage%5D%22%2C%22className%22%3A%22w-full%20resize-none%20rounded-xl%20border%20border-white%2F10%20bg-white%20px-4%20py-3%20text-black%20placeholder%3Atext-gray-500%20outline-none%20transition%20focus%3Aborder-%5B%239000ff%5D%22%7D"
                  name="message"
                  placeholder="Your message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  required
                  className="w-full resize-none rounded-xl border border-white/10 bg-white px-4 py-3 text-black placeholder:text-gray-500 outline-none transition focus:border-[#9000ff]"
                />

                <button data-component-id="src/components/ui/PopupForm.tsx:159:16" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="159" data-component-end-line="165" data-component-file="PopupForm.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22min-h-%5B54px%5D%20w-full%20rounded-full%20bg-%5B%239000ff%5D%20px-6%20text-%5B16px%5D%20font-bold%20text-white%20shadow-%5B0_10px_28px_rgba(0%2C0%2C0%2C0.35)%5D%20transition-transform%20duration-200%20hover%3Ascale-%5B1.02%5D%20disabled%3Acursor-not-allowed%20disabled%3Aopacity-70%22%7D"
                  type="submit"
                  disabled={isSubmitting}
                  className="min-h-[54px] w-full rounded-full bg-[#9000ff] px-6 text-[16px] font-bold text-white shadow-[0_10px_28px_rgba(0,0,0,0.35)] transition-transform duration-200 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSubmitting ? 'Sending...' : 'Send request'}
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div data-component-id="src/components/ui/PopupForm.tsx:169:12" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="169" data-component-end-line="181" data-component-file="PopupForm.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22w-full%20max-w-%5B460px%5D%20rounded-%5B28px%5D%20border%20border-white%2F10%20bg-%5B%233a3a3a%5D%2F95%20p-8%20text-center%20text-%5B%23b3b3b3%5D%20shadow-%5B0_20px_80px_rgba(0%2C0%2C0%2C0.45)%5D%22%7D"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-[460px] rounded-[28px] border border-white/10 bg-[#3a3a3a]/95 p-8 text-center text-[#b3b3b3] shadow-[0_20px_80px_rgba(0,0,0,0.45)]"
            >
              <h3 data-component-id="src/components/ui/PopupForm.tsx:177:14" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="177" data-component-end-line="177" data-component-file="PopupForm.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-%5B30px%5D%20font-bold%20text-white%22%2C%22textContent%22%3A%22Thank%20you!%22%7D" className="text-[30px] font-bold text-white">Thank you!</h3>
              <p data-component-id="src/components/ui/PopupForm.tsx:178:14" data-component-path="src/components/ui/PopupForm.tsx" data-component-line="178" data-component-end-line="180" data-component-file="PopupForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-4%20text-%5B16px%5D%20leading-%5B1.6%5D%20text-white%2F75%22%2C%22textContent%22%3A%22Your%20request%20has%20been%20sent%20successfully.%20We%20will%20contact%20you%20soon.%22%7D" className="mt-4 text-[16px] leading-[1.6] text-white/75">
                Your request has been sent successfully. We will contact you soon.
              </p>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// 'use client';

// import { useEffect, useState } from 'react';
// import { AnimatePresence, motion } from 'framer-motion';
// import emailjs from '@emailjs/browser';

// type PopupFormProps = {
//   isOpen: boolean;
//   onClose: () => void;
// };

// export default function PopupForm({ isOpen, onClose }: PopupFormProps) {
//   const [name, setName] = useState('');
//   const [phone, setPhone] = useState('');
//   const [email, setEmail] = useState('');
//   const [message, setMessage] = useState('');
//   const [showThankYou, setShowThankYou] = useState(false);
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const resetForm = () => {
//     setName('');
//     setPhone('');
//     setEmail('');
//     setMessage('');
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (isSubmitting) return;

//     try {
//       setIsSubmitting(true);

//       await emailjs.send(
//         process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID!,
//         process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID!,
//         {
//           name,
//           phone,
//           email,
//           message,
//         },
//         process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY!
//       );

//       setShowThankYou(true);
//       resetForm();

//       setTimeout(() => {
//         setShowThankYou(false);
//         onClose();
//       }, 2500);
//     } catch (error) {
//       console.error('EmailJS error:', error);
//       alert('Something went wrong. Please try again.');
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   useEffect(() => {
//     document.body.style.overflow = isOpen ? 'hidden' : '';
//     return () => {
//       document.body.style.overflow = '';
//     };
//   }, [isOpen]);

//   useEffect(() => {
//     if (!isOpen) {
//       setShowThankYou(false);
//     }
//   }, [isOpen]);

//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <motion.div
//           className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           exit={{ opacity: 0 }}
//           onClick={onClose}
//         >
//           {!showThankYou ? (
//             <motion.div
//               onClick={(e) => e.stopPropagation()}
//               initial={{ opacity: 0, scale: 0.96, y: 20 }}
//               animate={{ opacity: 1, scale: 1, y: 0 }}
//               exit={{ opacity: 0, scale: 0.96, y: 20 }}
//               transition={{ duration: 0.25 }}
//               className="h-[90vh] overflow-y-auto relative w-full max-w-[560px] rounded-[28px] border border-white/10 bg-[#3a3a3a]/95 p-6 text-[#b3b3b3] shadow-[0_20px_80px_rgba(0,0,0,0.45)] sm:p-8"
//             >
//               <button
//                 onClick={onClose}
//                 aria-label="Close popup"
//                 className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-xl text-white/60 transition hover:bg-white/5 hover:text-white"
//               >
//                 ×
//               </button>

//               <p className="pr-10 text-[18px] sm:text-[20px] text-white/80">Contact us</p>

//               <h3 className="mt-2 pr-10 text-[34px] font-bold leading-[1.1] text-white sm:text-[42px]">
//                 Let’s discuss your project
//               </h3>

//               <p className="mt-4 max-w-[460px] text-[14px] leading-[1.6] text-white/65 sm:text-[16px]">
//                 Leave your details and we will get back to you as soon as possible.
//               </p>

//               <form onSubmit={handleSubmit} className="mt-6 space-y-4">
//                 <input
//                   type="text"
//                   name="name"
//                   placeholder="Your name"
//                   value={name}
//                   onChange={(e) => setName(e.target.value)}
//                   required
//                   className="w-full rounded-xl border border-white/10 bg-white px-4 py-3 text-black placeholder:text-gray-500 outline-none transition focus:border-[#9000ff]"
//                 />

//                 <input
//                   type="tel"
//                   name="phone"
//                   placeholder="Phone number"
//                   value={phone}
//                   onChange={(e) => setPhone(e.target.value)}
//                   required
//                   inputMode="tel"
//                   className="w-full rounded-xl border border-white/10 bg-white px-4 py-3 text-black placeholder:text-gray-500 outline-none transition focus:border-[#9000ff]"
//                 />

//                 <input
//                   type="email"
//                   name="email"
//                   placeholder="Email"
//                   value={email}
//                   onChange={(e) => setEmail(e.target.value)}
//                   required
//                   className="w-full rounded-xl border border-white/10 bg-white px-4 py-3 text-black placeholder:text-gray-500 outline-none transition focus:border-[#9000ff]"
//                 />

//                 <textarea
//                   name="message"
//                   placeholder="Your message"
//                   value={message}
//                   onChange={(e) => setMessage(e.target.value)}
//                   rows={5}
//                   required
//                   className="w-full resize-none rounded-xl border border-white/10 bg-white px-4 py-3 text-black placeholder:text-gray-500 outline-none transition focus:border-[#9000ff]"
//                 />

//                 <button
//                   type="submit"
//                   disabled={isSubmitting}
//                   className="min-h-[54px] w-full rounded-full bg-[#9000ff] px-6 text-[16px] font-bold text-white shadow-[0_10px_28px_rgba(0,0,0,0.35)] transition-transform duration-200 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-70"
//                 >
//                   {isSubmitting ? 'Sending...' : 'Send request'}
//                 </button>
//               </form>
//             </motion.div>
//           ) : (
//             <motion.div
//               onClick={(e) => e.stopPropagation()}
//               initial={{ opacity: 0, scale: 0.96, y: 20 }}
//               animate={{ opacity: 1, scale: 1, y: 0 }}
//               exit={{ opacity: 0, scale: 0.96, y: 20 }}
//               transition={{ duration: 0.25 }}
//               className="w-full max-w-[460px] rounded-[28px] border border-white/10 bg-[#3a3a3a]/95 p-8 text-center text-[#b3b3b3] shadow-[0_20px_80px_rgba(0,0,0,0.45)]"
//             >
//               <h3 className="text-[30px] font-bold text-white">Thank you!</h3>
//               <p className="mt-4 text-[16px] leading-[1.6] text-white/75">
//                 Your request has been sent successfully. We will contact you soon.
//               </p>
//             </motion.div>
//           )}
//         </motion.div>
//       )}
//     </AnimatePresence>
//   );
// }
